import { useRef } from 'react';
import { ArrowRight, Bug, Leaf, Shield, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { useAuth } from '../../hooks/use-auth';
import { useGSAP } from '../../hooks/use-gsap';

const features = [
  {
    icon: Zap,
    title: 'Instant Results',
    description: 'Get accurate plant identification in seconds',
    color: 'emerald'
  },
  {
    icon: Shield,
    title: 'Secure Storage',
    description: 'Save and access your plant history anytime',
    color: 'blue'
  },
  {
    icon: Leaf,
    title: 'Expert Care Tips',
    description: 'Receive detailed plant care instructions',
    color: 'purple'
  }
];

const stats = [
  { label: 'Plants Identified', value: '1K+', endValue: 1006 },
  { label: 'Active Users', value: '50+', endValue: 50 },
  { label: 'Success Rate', value: '99%', endValue: 99, suffix: '%' }
];

const quickLinks = [
  {
    title: 'Plant Identifier',
    description: 'Identify any plant instantly',
    icon: Leaf,
    path: '/identifier',
    color: 'emerald'
  },
  {
    title: 'Disease Detection',
    description: 'Check plant health & diseases',
    icon: Bug,
    path: '/disease-detection',
    color: 'amber'
  }
];

export function CTASection() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Refs for animations
  const titleRef = useRef(null);
  const featuresRef = useRef(null);
  const statsRef = useRef(null);
  const ctaRef = useRef(null);
  const linksRef = useRef(null);
  const statRefs = useRef([]);

  // Configure animations using the custom hook
  useGSAP([
    {
      ref: titleRef,
      preset: 'fadeInDown',
      scrollTrigger: {
        start: 'top 60%'
      }
    },
    {
      ref: featuresRef,
      preset: 'fadeInUp',
      options: {
        to: { duration: 1.2 }
      }
    },
    {
      ref: statsRef,
      preset: 'zoomIn',
      scrollTrigger: {
        start: 'top 70%',
        onEnter: () => {
          // Animate each stat number
          stats.forEach((stat, index) => {
            const el = statRefs.current[index];
            if (!el) return;

            // let startValue = 0;
            const duration = 2;

            gsap.to(el, {
              duration,
              innerText: stat.endValue,
              snap: { innerText: 1 },
              ease: 'power2.out',
              onUpdate: () => {
                const current = Math.round(gsap.getProperty(el, 'innerText'));
                el.innerText = current.toLocaleString() + (stat.suffix || '+');
              }
            });
          });
        }
      }
    },
    {
      ref: ctaRef,
      preset: 'fadeInUp',
      options: {
        to: { duration: 0.8 }
      }
    },
    {
      ref: linksRef,
      preset: 'fadeInUp',
      options: {
        to: { duration: 0.5, stagger: 0.2 }
      }
    }
  ]);

  return (
    <div className="py-24 relative overflow-hidden">
      {/* Background remains the same */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-teal-50/50 to-blue-50/30">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(0,0,0,0.1) 1px, transparent 0)`,
              backgroundSize: '24px 24px'
            }}
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Main Content */}
        <div ref={titleRef} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Transform Your Plant Care Journey
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join thousands of plant enthusiasts who trust our AI-powered
            platform for accurate plant identification and expert care guidance.
          </p>
        </div>

        {/* Features Grid */}
        <div ref={featuresRef} className="grid md:grid-cols-3 gap-8 mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg p-6 transform transition-all duration-300 hover:shadow-xl"
            >
              <div
                className={`bg-${feature.color}-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4`}
              >
                <feature.icon className={`w-6 h-6 text-${feature.color}-600`} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div ref={statsRef} className="grid grid-cols-3 gap-8 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div
                ref={el => (statRefs.current[index] = el)}
                className="text-4xl font-bold text-emerald-600 mb-2"
              >
                0{stat.suffix || '+'}
              </div>
              <div className="text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div ref={ctaRef} className="text-center">
          <button
            onClick={() => navigate(user ? '/dashboard' : '/signup')}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-xl font-medium text-lg inline-flex items-center gap-2 hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 shadow-lg"
          >
            {user ? 'Go to Dashboard' : 'Start Identifying Plants'}
            <ArrowRight className="w-5 h-5" />
          </button>

          {user && (
            <div ref={linksRef} className="mt-8 flex justify-center gap-4">
              {quickLinks.map((link, index) => (
                <button
                  key={index}
                  onClick={() => navigate(link.path)}
                  className={`flex items-center gap-3 px-6 py-3 bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group`}
                >
                  <div
                    className={`w-10 h-10 rounded-lg bg-${link.color}-100 flex items-center justify-center group-hover:bg-${link.color}-200 transition-colors`}
                  >
                    <link.icon className={`w-5 h-5 text-${link.color}-600`} />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">
                      {link.title}
                    </div>
                    <div className="text-sm text-gray-600">
                      {link.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {!user && (
            <p className="mt-4 text-gray-600">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/signin')}
                className="text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
